/*
  kinetic-first-order — behaviour for snippets/kinetic-first-order.liquid,
  the 40% first-order unlock card on the PDP block and the landing buy box.

  Initialises every [data-kfo-panel] on the page. Flow: validate → Klaviyo
  client subscribe (202, with a locale-less retry on a 4xx) → POST the
  minting endpoint {email} → fetch('/discount/<code>') so the code rides
  into checkout → show the code. Subscribe and mint are both idempotent
  (the code is derived from the email), so any failure is a retryable
  inline error. Codes are never emailed.

  Country is decided client-side from Shopify's IP endpoint — never from
  the market context, which follows the URL/cookie (the country picker) —
  and an unknown country fails closed. The endpoint also answers 403 for a
  Belgian IP, which hides the card.

  Hosts react through events on the panel (they bubble):
    kfo:reveal                  the card became visible
    kfo:unlocked {code,restore} a code is in hand (fresh or restored)
    kfo:hide                    the server refused the visitor's country
  plus `kinetic:first-order:unlocked` on document and
  html[data-kfo-unlocked] for anything page-wide (the volume ladder).
*/
(function () {
  if (window.KineticFirstOrder) return;

  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);
  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  // Preview-only switches, for reviewing the card from Belgium:
  //   ?kfo_geo=NL          treat the visitor as that country (session-wide,
  //                        the pop-ups read the same cache)
  //   ?kfo_preview=unlocked show the unlocked state with a dummy code
  // Ignored on the published theme (Shopify.theme.role === 'main'), so they
  // can never open the offer to Belgian visitors on the live store.
  var previewUnlocked = false;
  (function () {
    try {
      var theme = window.Shopify && window.Shopify.theme;
      if (!theme || theme.role === 'main') return;
      var geo = location.search.match(/[?&]kfo_geo=([A-Za-z]{2})\b/);
      if (geo) sessionStorage.setItem('oleus-geo-country', geo[1].toUpperCase());
      previewUnlocked = /[?&]kfo_preview=unlocked\b/.test(location.search);
    } catch (e) {}
  })();

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }

  function emit(target, name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, detail: detail || {} });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail || {});
    }
    target.dispatchEvent(ev);
  }

  // Shared with the scratch pop-up and the landing buy box: one lookup per
  // session, one in-flight request per page.
  function geoAllows(geo, cb) {
    if (geo === 'all') { cb(true); return; }
    var apply = function (c) { cb(c ? (geo === 'be' ? c === 'BE' : c !== 'BE') : false); };
    var cached = null;
    try { cached = sessionStorage.getItem('oleus-geo-country'); } catch (e) {}
    if (cached) { apply(cached); return; }
    if (!window.__oleusGeoCountry) {
      window.__oleusGeoCountry = fetch('/browsing_context_suggestions.json')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var c = (d && d.detected_values && d.detected_values.country && d.detected_values.country.handle) || '';
          if (c) { try { sessionStorage.setItem('oleus-geo-country', c); } catch (e) {} }
          return c;
        })
        .catch(function () { return ''; });
    }
    window.__oleusGeoCountry.then(apply);
  }

  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  function init(panel) {
    if (!panel || panel.__kfoInit) return;
    panel.__kfoInit = true;

    var lockedEl = panel.querySelector('[data-kfo-locked]');
    var doneEl   = panel.querySelector('[data-kfo-done]');
    var formEl   = panel.querySelector('[data-kfo-form]');
    var emailEl  = panel.querySelector('[data-kfo-email]');
    var submitEl = panel.querySelector('[data-kfo-submit]');
    var submitLb = panel.querySelector('[data-kfo-submit-label]');
    var statusEl = panel.querySelector('[data-kfo-status]');
    var codeWrap = panel.querySelector('[data-kfo-codewrap]');
    var codeEl   = panel.querySelector('[data-kfo-code]');
    var copyBtn  = panel.querySelector('[data-kfo-copy]');
    if (!formEl || !emailEl) return;

    var attr = function (n, d) { return panel.getAttribute('data-kfo-' + n) || d; };
    var GEO       = attr('geo', 'not-be');
    var CAMPAIGN  = attr('campaign', 'scratch40');
    var CODE_API  = attr('code-api', '');
    var COMPANY   = attr('company-id', '');
    var LIST      = attr('list', '');
    var SOURCE    = attr('source', '');
    var LOCALE    = attr('locale', '');
    var FLOW_STEP = attr('flow-step', '');
    var PREFIX    = attr('event-prefix', 'kinetic_first_order');
    var MSG_LOADING = attr('loading-label', 'Unlocking…');
    var MSG_INVALID = attr('invalid-message', 'Please enter a valid email address.');
    var MSG_ERROR   = attr('error-message', 'Something went wrong, please try again in a moment.');

    // The claim flag is shared with the scratch pop-up, so unlocking here
    // suppresses it (and, through its offer guard, the 10% pop-up too).
    var KEY_DONE = 'oleus-popup:' + CAMPAIGN + ':done';
    var KEY_CODE = 'oleus-offer:' + CAMPAIGN + ':code';
    // The scratch pop-up files the same visitor's code under its own key.
    var KEY_POPUP_CODE = 'oleus-popup:' + CAMPAIGN + ':code';

    var settled = false;
    function settle(v) {
      if (settled) return;
      settled = true;
      if (typeof panel.__kfoSettle === 'function') panel.__kfoSettle(!!v);
    }

    function track(suffix) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: PREFIX + '_' + suffix, popup_campaign: CAMPAIGN, popup_flow_step: FLOW_STEP });
      } catch (e) {}
    }

    var busy = false;
    var submitLabel = submitLb ? submitLb.textContent : '';

    function setStatus(msg) {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
      statusEl.hidden = !msg;
    }
    function setBusy(b) {
      busy = b;
      if (submitEl) submitEl.disabled = b;
      if (submitLb) submitLb.textContent = b ? MSG_LOADING : submitLabel;
    }

    function reveal(silent) {
      if (!panel.hidden) return;
      panel.hidden = false;
      emit(panel, 'kfo:reveal');
      if (!silent && !inDesignMode) track('shown');
    }

    function showUnlocked(code, isRestore) {
      panel.setAttribute('data-kfo-state', 'unlocked');
      if (lockedEl) lockedEl.hidden = true;
      if (doneEl) doneEl.hidden = false;
      if (code && codeEl && codeWrap) {
        codeEl.textContent = code;
        codeWrap.hidden = false;
      }
      reveal(true);
      setBusy(false);
      document.documentElement.setAttribute('data-kfo-unlocked', '');
      emit(panel, 'kfo:unlocked', { code: code || '', restore: !!isRestore });
      emit(document, 'kinetic:first-order:unlocked', { code: code || '', restore: !!isRestore });
      if (!isRestore) track('unlocked');
    }

    // Shopify answers with a redirect and sets the discount cookie, so the
    // code rides into checkout without the visitor typing it.
    function applyDiscount(code) {
      if (!code || inDesignMode) return;
      fetch(routesRoot + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(function () {});
    }

    function requestCode(email) {
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 8000);
      return fetch(CODE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email }),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (r) {
        if (r.status === 403) {
          var geoErr = new Error('scratch-code geo');
          geoErr.geo = true;
          throw geoErr;
        }
        if (!r.ok) throw new Error('scratch-code ' + r.status);
        return r.json();
      }).then(function (d) {
        clearTimeout(timer);
        if (!d || !d.code) throw new Error('scratch-code empty');
        return d.code;
      }).catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
    }

    function subscribe(email, includeLocale) {
      var attrs = { email: email };
      if (includeLocale && LOCALE) attrs.locale = LOCALE;
      if (FLOW_STEP) attrs.properties = { flow_step: FLOW_STEP };
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: SOURCE,
            profile: { data: { type: 'profile', attributes: attrs } }
          },
          relationships: { list: { data: { type: 'list', id: LIST } } }
        }
      };
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(COMPANY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2026-04-15' },
        body: JSON.stringify(payload)
      });
    }

    if (copyBtn && codeEl) {
      var copyTimer = 0;
      copyBtn.addEventListener('click', function () {
        var code = (codeEl.textContent || '').trim();
        if (!code) return;
        var flash = function () {
          copyBtn.textContent = copyBtn.getAttribute('data-copied');
          clearTimeout(copyTimer);
          copyTimer = setTimeout(function () {
            copyBtn.textContent = copyBtn.getAttribute('data-label');
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(flash).catch(function () { legacyCopy(code); flash(); });
        } else {
          legacyCopy(code);
          flash();
        }
        track('code_copied');
      });
    }

    emailEl.addEventListener('input', function () {
      if (emailEl.getAttribute('aria-invalid')) {
        emailEl.removeAttribute('aria-invalid');
        setStatus('');
      }
    });

    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      var email = (emailEl.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailEl.setAttribute('aria-invalid', 'true');
        setStatus(MSG_INVALID);
        try { emailEl.focus(); } catch (err) {}
        return;
      }
      emailEl.removeAttribute('aria-invalid');
      setStatus('');
      // Editor preview — no real POSTs.
      if (inDesignMode) { showUnlocked('PREVIEW-CODE'); return; }
      if (!LIST || !CODE_API) { setStatus(MSG_ERROR); return; }
      setBusy(true);
      subscribe(email, true).then(function (res) {
        if (res.status === 202) return null;
        // Older Klaviyo revisions reject the locale attribute — retry plain.
        if (res.status >= 400 && res.status < 500) return subscribe(email, false);
        return res;
      }).then(function (res2) {
        if (res2 && res2.status !== 202) throw new Error('klaviyo ' + res2.status);
        return requestCode(email);
      }).then(function (code) {
        lsSet(KEY_DONE, '1');
        lsSet(KEY_CODE, code);
        showUnlocked(code);
        applyDiscount(code);
      }).catch(function (err) {
        setBusy(false);
        if (err && err.geo) {
          // The endpoint saw a country the offer excludes (a Belgian IP the
          // client lookup missed). No code, no card.
          panel.hidden = true;
          emit(panel, 'kfo:hide');
          track('geo_refused');
          return;
        }
        console.error('[kinetic-first-order] unlock failed', err);
        track('error');
        setStatus(MSG_ERROR);
      });
    });

    // Boot ---------------------------------------------------------
    if (inDesignMode) { reveal(true); settle(false); return; }
    // Nothing stored and no /discount/ call: a look at the state only.
    if (previewUnlocked) { settle(true); showUnlocked('SCRATCH40-PREVIEW', true); return; }

    var storedCode = lsGet(KEY_CODE) || lsGet(KEY_POPUP_CODE);
    if (storedCode) {
      // Survives navigation and return visits; the cart cookie may have
      // lapsed since, so re-apply it silently. Mirror a pop-up-minted code
      // onto this key so every surface agrees from here on.
      if (!lsGet(KEY_CODE)) lsSet(KEY_CODE, storedCode);
      settle(true);
      showUnlocked(storedCode, true);
      applyDiscount(storedCode);
      return;
    }
    // Claimed but no code stored (another device, or the legacy email
    // path): deliberately no early return. Minting is idempotent per email,
    // so re-entering it hands back the code they already have, and falling
    // through keeps them behind the geo gate below.

    geoAllows(GEO, function (ok) {
      // An event QR gate (kinetic-gate) owns its visitors outright and has
      // already handed over its own code — never offer a second one. Read
      // here, after the shared geo lookup, because the gate drops its
      // ownership attribute once its own country check fails.
      if (document.querySelector('[data-section-type="kinetic-gate"][data-kgt-owns]')) {
        settle(true);
        return;
      }
      settle(ok || !!lsGet(KEY_DONE));
      // Belgium, or a country we could not determine — no card.
      if (ok) reveal();
    });
  }

  // While the 1+1 campaign runs (sections/kinetic-entry-popup.liquid sets
  // html[data-bogo-active] and hides the card with CSS), the 40% stands down
  // completely: no reveal, no restore of an earlier code (whose /discount/
  // call would replace the 1+1 at checkout), and the offer guard settles
  // false so nothing waits on it.
  function bogoActive() {
    return document.documentElement.hasAttribute('data-bogo-active');
  }

  function boot() {
    var panels = document.querySelectorAll('[data-kfo-panel]');
    for (var i = 0; i < panels.length; i++) {
      if (bogoActive() && !inDesignMode) {
        if (typeof panels[i].__kfoSettle === 'function') panels[i].__kfoSettle(false);
        continue;
      }
      init(panels[i]);
    }
  }

  window.KineticFirstOrder = { init: init, boot: boot };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
